import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Alert, 
  LinearProgress, 
  Button,
  IconButton,
  Pagination,
  MenuItem,
  Select,
  FormControl,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import DescriptionIcon from '@mui/icons-material/Description';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const FileUploadRCM = ({ onRCMDataChange, companyId, validAppNames }) => {
  const [rcm_data, setRCMData] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [fileName, setFileName] = useState('');
  const [validationError, setValidationError] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [fileProcessed, setFileProcessed] = useState(false);
  // Pagination state
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [copySuccess, setCopySuccess] = useState(false);

  // Required columns for RCM upload
  const requiredColumns = ['APP_NAME', 'RISK_ID', 'CONTROL_ID', 'COMPANY_ID'];

  // Reset file processed flag when component mounts
  useEffect(() => {
    setFileProcessed(false);
  }, []);

  // Copy to clipboard function
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(
      () => {
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      },
      (err) => {
        console.error('Could not copy text: ', err);
      }
    );
  };

  // Handle drag over event
  const handleDragOver = (e) => {
    e.preventDefault();
    setDragging(true);
  };

  // Handle drag leave event
  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragging(false);
  };

  // Handle file drop event
  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    handleFile(file);
  };

  // Handle file input change event
  const handleFileInputChange = (e) => {
    const file = e.target.files[0];
    handleFile(file);
  };

  // Clear file and reset state
  const handleClearFile = () => {
    setFileName('');
    setRCMData([]);
    onRCMDataChange([]);
    setValidationError('');
    setFileProcessed(false);
    setPage(1);
  };

  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  // Download template
  const handleDownloadTemplate = () => {
    // First add a readme/description row with information about the fields
    const readme = [
      '"README"',
      '"This template is for Risk and Control Matrix (RCM) upload."',
      '"Please ensure all required fields are filled correctly."',
      '""',
      '""',
      '""'
    ].join(',');
    
    const fieldDescriptions = [
      '"FIELD DESCRIPTIONS"',
      '"Unique application identifier/name"',
      '"Unique risk identifier"',
      '"Description of the risk"',
      '"Unique control identifier"',
      '"Description of the control"',
      `"Company ID (MUST be exactly: "${companyId || 'COMPANY_ID'}")"`
    ].join(',');
    
    const emptyRow = ['""', '""', '""', '""', '""', '""'].join(',');
    
    // Create CSV content for template
    const headers = ['APP_NAME', 'RISK_ID', 'RISK_DESCRIPTION', 'CONTROL_ID', 'CONTROL_DESCRIPTION', 'COMPANY_ID'].join(',');
    
    // If we have valid app names, use the first one as an example
    const exampleAppName = validAppNames && validAppNames.length > 0 
      ? (validAppNames[0].APP_NAME || validAppNames[0].id) 
      : 'App Name 1';
    
    const sampleRow1 = [
      `"${exampleAppName}"`, 
      '"RISK001"', 
      '"Sample Risk Description"', 
      '"CTRL001"', 
      '"Sample Control Description"', 
      `"${companyId || 'COMPANY_ID'}"`
    ].join(',');
    
    const sampleRow2 = [
      `"${validAppNames && validAppNames.length > 1 ? (validAppNames[1].APP_NAME || validAppNames[1].id) : 'App Name 2'}"`, 
      '"RISK002"', 
      '"Another Risk Description"', 
      '"CTRL002"', 
      '"Another Control Description"', 
      `"${companyId || 'COMPANY_ID'}"`
    ].join(',');
    
    const csvContent = [readme, fieldDescriptions, emptyRow, headers, sampleRow1, sampleRow2].join('\n');
    
    // Create a blob and download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    
    link.setAttribute('href', url);
    link.setAttribute('download', `rcm_template_${companyId ? companyId : 'template'}.csv`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Handle file upload logic
  const handleFile = (file) => {
    // Reset validation state
    setValidationError('');
    setIsValidating(true);
    setPage(1);
    
    if (!file) {
      setIsValidating(false);
      return;
    }
    
    const fileType = file.type;

    if (fileType === 'text/csv') {
      setFileName(file.name); // Display file name
      parseCSV(file);
    } else {
      setValidationError('File is not supported. Make sure to upload a CSV file.');
      setIsValidating(false);
    }
  };

  // CSV Parsing function (handling quotes and commas)
  const parseCSV = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target.result;
        const rows = content.split('\n');

        // Filter out metadata rows (first few rows that contain README, etc.)
        const headerRowIndex = rows.findIndex(row => row.includes('APP_NAME,RISK_ID'));
        
        if (headerRowIndex === -1 || rows.length < headerRowIndex + 2) {
          setValidationError('CSV file is not properly formatted or is missing data rows');
          setRCMData([]);
          onRCMDataChange([]);
          setIsValidating(false);
          setFileProcessed(false);
          return;
        }

        // Parse header row
        const header = parseCSVRow(rows[headerRowIndex]);
        
        // Validate required columns
        const missingColumns = requiredColumns.filter(col => !header.includes(col));
        if (missingColumns.length > 0) {
          setValidationError(`Missing required columns: ${missingColumns.join(', ')}`);
          setRCMData([]);
          onRCMDataChange([]);
          setIsValidating(false);
          setFileProcessed(false);
          return;
        }

        // Parse the rest of the rows
        const data = rows.slice(headerRowIndex + 1)
          .filter(row => row.trim() !== '') // Filter out empty rows
          .map((row) => {
            const values = parseCSVRow(row);
            const rowData = {};
            header.forEach((col, index) => {
              rowData[col.trim()] = values[index]?.trim() || ''; // Handling missing values
            });
            return rowData;
          });

        // Validate that there is at least one row of data
        if (data.length === 0) {
          setValidationError('CSV file contains headers but no data rows');
          setRCMData([]);
          onRCMDataChange([]);
          setIsValidating(false);
          setFileProcessed(false);
          return;
        }

        // Validate COMPANY_ID if provided - compare exact string match
        if (companyId) {
          const invalidCompanyRows = data.filter(row => String(row.COMPANY_ID).trim() !== String(companyId).trim());
          if (invalidCompanyRows.length > 0) {
            // Get the unique invalid company IDs to display in the error message
            const uniqueInvalidCompanyIds = [...new Set(invalidCompanyRows.map(row => row.COMPANY_ID))];
            const invalidCompanyDisplay = uniqueInvalidCompanyIds.slice(0, 3).map(id => `"${id}"`).join(', ');
            
            // Calculate how many rows have invalid company IDs
            const invalidRowCount = invalidCompanyRows.length;
            const totalRowCount = data.length;
            
            setValidationError(
              `Invalid COMPANY_ID found in ${invalidRowCount} out of ${totalRowCount} rows. 
              Examples: ${invalidCompanyDisplay}${uniqueInvalidCompanyIds.length > 3 ? '...' : ''}.
              All records must have exactly COMPANY_ID: "${companyId}" (case-sensitive, no leading/trailing spaces).
              Please update your spreadsheet or download our template.`
            );
            setRCMData([]);
            onRCMDataChange([]);
            setIsValidating(false);
            setFileProcessed(false);
            return;
          }
        }

        // Validate APP_NAME if validAppNames array is provided
        if (validAppNames && validAppNames.length > 0) {
          const invalidAppRows = data.filter(row => !validAppNames.some(app => 
            app.id === row.APP_NAME || app.APP_NAME === row.APP_NAME));
          
          if (invalidAppRows.length > 0) {
            // Extract the invalid app names for the error message (limit to first 3)
            const invalidApps = [...new Set(invalidAppRows.map(row => row.APP_NAME))].slice(0, 3);
            
            // Create a list of valid app names for reference (limited to first 5)
            const validAppNamesList = validAppNames
              .map(app => app.APP_NAME || app.id)
              .slice(0, 5)
              .map(name => `"${name}"`)
              .join(', ');
            
            setValidationError(
              `Invalid application names found: ${invalidApps.map(app => `"${app}"`).join(', ')}${invalidApps.length > 3 ? '...' : ''}. 
              All applications must belong to the current company. 
              Valid applications include: ${validAppNamesList}${validAppNames.length > 5 ? '...' : ''}`
            );
            setRCMData([]);
            onRCMDataChange([]);
            setIsValidating(false);
            setFileProcessed(false);
            return;
          }
        }

        setRCMData(data);
        onRCMDataChange(data); // Pass controls data to parent
        setIsValidating(false);
        setFileProcessed(true); // Mark file as successfully processed
      } catch (error) {
        console.error('Error parsing CSV file:', error);
        setValidationError(`Error parsing CSV file: ${error.message}`);
        setIsValidating(false);
        setFileProcessed(false);
      }
    };
    
    reader.onerror = (error) => {
      console.error('Error reading file:', error);
      setValidationError(`Error reading file: ${error}`);
      setIsValidating(false);
      setFileProcessed(false);
    };
    
    reader.readAsText(file);
  };

  // Helper function to parse a CSV row
  const parseCSVRow = (row) => {
    const values = [];
    let inQuotes = false;
    let currentValue = '';
    
    for (let i = 0; i < row.length; i++) {
      const char = row[i];

      if (char === '"' && (i === 0 || row[i - 1] !== '"')) {
        // Toggle inQuotes when encountering a single quote
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        // If not in quotes, treat comma as separator
        values.push(currentValue);
        currentValue = '';
      } else {
        // Add char to the current value
        currentValue += char;
      }
    }
    values.push(currentValue); // Push the last value
    
    // Clean up quotes from the values
    return values.map(val => {
      // Remove surrounding quotes if they exist
      if (val.startsWith('"') && val.endsWith('"')) {
        return val.substring(1, val.length - 1);
      }
      return val;
    });
  };

  // Calculate pagination values
  const pageCount = Math.ceil(rcm_data.length / rowsPerPage);
  const startIndex = (page - 1) * rowsPerPage;
  const endIndex = Math.min(startIndex + rowsPerPage, rcm_data.length);
  const paginatedData = rcm_data.slice(startIndex, endIndex);

  return (
    <Box sx={{ padding: '16px 24px' }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6" sx={{ color: '#333', fontSize: '1.25rem', fontWeight: 500 }}>
          Upload Risk and Controls Matrix
        </Typography>
        <Box>
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadTemplate}
            size="small"
            sx={{ 
              textTransform: 'none',
              borderColor: 'rgba(0,0,0,0.12)',
              color: '#046FB2',
              borderRadius: '6px',
              '&:hover': {
                borderColor: '#046FB2',
                backgroundColor: 'rgba(4, 111, 178, 0.04)'
              }
            }}
          >
            Download Template
          </Button>
        </Box>
      </Box>

      <Typography variant="body2" sx={{ mb: 1, color: '#555' }}>
        Upload your Risk and Controls Matrix CSV file to map risks to controls across applications. The file should contain the required columns: <strong>APP_NAME</strong>, <strong>RISK_ID</strong>, <strong>CONTROL_ID</strong>, and <strong>COMPANY_ID</strong>.
      </Typography>
      
      {companyId && (
        <Box sx={{ 
          mb: 2, 
          p: 1.5, 
          bgcolor: 'rgba(4, 111, 178, 0.08)', 
          borderRadius: '6px',
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: 1
        }}>
          <Box>
            <Typography variant="body2" color="primary" sx={{ fontWeight: 500 }}>
              Important: For this company, make sure to use exactly <strong>COMPANY_ID: "{companyId}"</strong> in your spreadsheet.
            </Typography>
            
          </Box>
          <Button
            size="small"
            variant="outlined"
            onClick={() => copyToClipboard(companyId)}
            sx={{ 
              minWidth: 'auto',
              ml: { xs: 0, sm: 2 },
              fontSize: '0.75rem',
              textTransform: 'none',
              borderColor: 'rgba(4, 111, 178, 0.5)',
              color: '#046FB2',
              height: '28px',
              padding: '0 8px',
              whiteSpace: 'nowrap',
              transition: 'all 0.2s',
              '&:hover': {
                borderColor: '#046FB2',
                backgroundColor: 'rgba(4, 111, 178, 0.04)'
              }
            }}
          >
            {copySuccess ? 'Copied!' : 'Copy ID'}
          </Button>
        </Box>
      )}
      
      {validAppNames && validAppNames.length > 0 && (
        <Accordion 
          disableGutters 
          elevation={0}
          sx={{ 
            mb: 3, 
            border: '1px solid rgba(0,0,0,0.08)',
            borderRadius: '6px',
            '&:before': { display: 'none' },
            '& .MuiAccordionSummary-root': {
              minHeight: '48px',
              borderRadius: '6px'
            }
          }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            sx={{ 
              backgroundColor: 'rgba(0,0,0,0.02)',
              color: 'text.secondary'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <InfoOutlinedIcon sx={{ fontSize: '1rem', mr: 1, color: '#046FB2' }} />
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                View Valid Applications for this Company
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ p: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Your file must reference only applications that belong to the current company. Use the exact APP_NAME values shown below:
            </Typography>
            <Box sx={{ maxHeight: '150px', overflow: 'auto', mt: 1 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: 'rgba(0,0,0,0.03)' }}>
                    <th style={{ padding: '8px 16px', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600 }}>APP_NAME</th>
                    <th style={{ padding: '8px 16px', textAlign: 'left', fontSize: '0.75rem', fontWeight: 600 }}>Description</th>
                  </tr>
                </thead>
                <tbody>
                  {validAppNames.map((app, index) => (
                    <tr key={index} style={{ backgroundColor: index % 2 === 0 ? 'white' : '#f9fafb' }}>
                      <td style={{ padding: '6px 16px', fontSize: '0.75rem', fontWeight: 500, color: '#046FB2' }}>
                        {app.APP_NAME || app.id}
                      </td>
                      <td style={{ padding: '6px 16px', fontSize: '0.75rem', color: 'text.secondary' }}>
                        {app.APP_DESCRIPTION || 'No description'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          </AccordionDetails>
        </Accordion>
      )}
      
      {!rcm_data.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            border: dragging 
              ? '2px solid #046FB2' 
              : '2px dashed rgba(0, 0, 0, 0.12)',
            borderRadius: '8px',
            padding: '32px 20px',
            textAlign: 'center',
            cursor: 'pointer',
            backgroundColor: dragging 
              ? 'rgba(4, 111, 178, 0.04)' 
              : '#fafafa',
            marginBottom: '20px',
            transition: 'all 0.2s ease',
            position: 'relative',
            overflow: 'hidden'
          }}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => document.getElementById('rcm-file-input').click()}
        >
          {isValidating && (
            <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0 }}>
              <LinearProgress sx={{ height: 4 }} />
            </Box>
          )}
          
          <input
            type="file"
            accept=".csv"
            onChange={handleFileInputChange}
            style={{ display: 'none' }}
            id="rcm-file-input"
          />
          
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <CloudUploadIcon sx={{ fontSize: 48, color: '#046FB2', mb: 1.5, opacity: 0.7 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 0.5, color: '#333' }}>
              Drag & drop your file here
            </Typography>
            <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
              or
            </Typography>
            <Button
              variant="contained"
              size="small"
              startIcon={<DescriptionIcon />}
              onClick={() => document.getElementById('rcm-file-input').click()}
              sx={{ 
                textTransform: 'none', 
                backgroundColor: '#046FB2',
                boxShadow: 'none',
                borderRadius: '6px',
                px: 2,
                '&:hover': {
                  backgroundColor: '#035d94',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }
              }}
            >
              Browse Files
            </Button>
            <Typography variant="caption" sx={{ mt: 2, color: '#777' }}>
              Supported format: CSV file
            </Typography>
          </Box>
        </Paper>
      )}

      {/* File name display */}
      {fileName && !validationError && (
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          backgroundColor: 'rgba(4, 111, 178, 0.08)', 
          p: 1.5, 
          pl: 2,
          borderRadius: '6px', 
          mb: 2
        }}>
          <DescriptionIcon sx={{ color: '#046FB2', mr: 1.5, fontSize: '1.25rem' }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 500, color: '#333' }}>
              {fileName}
            </Typography>
            <Typography variant="caption" sx={{ color: '#666' }}>
              {rcm_data.length} rows • CSV file 
            </Typography>
          </Box>
          <IconButton size="small" onClick={handleClearFile}>
            <HighlightOffIcon sx={{ fontSize: '1.25rem', color: '#777' }} />
          </IconButton>
        </Box>
      )}

      {/* Validation Error message */}
      {validationError && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 2, 
            '& .MuiAlert-message': { fontWeight: 400 } 
          }}
        >
          {validationError}
        </Alert>
      )}

      {/* Data preview */}
      {rcm_data.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Typography variant="subtitle2" sx={{ color: '#333', fontWeight: 500 }}>
              Data Preview
            </Typography>
            <Typography variant="caption" sx={{ color: '#666' }}>
              Showing {startIndex + 1}-{endIndex} of {rcm_data.length} rows
            </Typography>
          </Box>
          
          <Box sx={{ 
            border: '1px solid rgba(224, 224, 224, 1)', 
            borderRadius: '6px', 
            overflow: 'hidden',
            mb: 2
          }}>
            <Box sx={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ backgroundColor: 'rgba(4, 111, 178, 0.04)' }}>
                    {Object.keys(rcm_data[0]).map((header, index) => (
                      <th
                        key={index}
                        style={{
                          padding: '10px 16px',
                          textAlign: 'left',
                          fontSize: '0.75rem',
                          color: '#333',
                          fontWeight: 600,
                          borderBottom: '1px solid rgba(224, 224, 224, 1)',
                          whiteSpace: 'nowrap',
                          backgroundColor: requiredColumns.includes(header) ? 'rgba(4, 111, 178, 0.08)' : 'rgba(4, 111, 178, 0.04)'
                        }}
                      >
                        {header}
                        {requiredColumns.includes(header) && (
                          <span style={{ color: '#046FB2', marginLeft: '2px' }}>*</span>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {paginatedData.map((row, rowIndex) => (
                    <tr key={rowIndex} style={{ backgroundColor: rowIndex % 2 === 0 ? 'white' : '#f9fafb' }}>
                      {Object.entries(row).map(([key, value], index) => (
                        <td
                          key={index}
                          style={{
                            fontSize: '0.75rem',
                            padding: '8px 16px',
                            borderBottom: rowIndex < paginatedData.length - 1 ? '1px solid rgba(224, 224, 224, 1)' : 'none',
                            color: '#333',
                            maxWidth: '200px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {value || '-'}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          </Box>
          
          {/* Pagination controls */}
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            mb: 2
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2" sx={{ mr: 1, color: '#666' }}>
                Rows per page:
              </Typography>
              <FormControl variant="standard" size="small">
                <Select
                  value={rowsPerPage}
                  onChange={handleChangeRowsPerPage}
                  sx={{ 
                    fontSize: '0.875rem',
                    '& .MuiSelect-select': { 
                      py: 0.5, 
                      minWidth: '60px' 
                    }
                  }}
                >
                  <MenuItem value={5}>5</MenuItem>
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                </Select>
              </FormControl>
            </Box>
            
            <Pagination
              count={pageCount}
              page={page}
              onChange={handleChangePage}
              size="small"
              color="primary"
              showFirstButton
              showLastButton
              siblingCount={1}
              boundaryCount={1}
              sx={{
                '& .MuiPaginationItem-root': {
                  fontSize: '0.875rem',
                  minWidth: '28px',
                  height: '28px',
                  borderRadius: '4px',
                },
                '& .Mui-selected': {
                  backgroundColor: 'rgba(4, 111, 178, 0.12)',
                  '&:hover': {
                    backgroundColor: 'rgba(4, 111, 178, 0.2)',
                  }
                }
              }}
            />
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleClearFile}
              sx={{ 
                textTransform: 'none',
                borderColor: 'rgba(0,0,0,0.12)',
                color: 'text.secondary',
                borderRadius: '6px',
                '&:hover': {
                  borderColor: 'text.primary',
                  backgroundColor: 'rgba(0,0,0,0.04)'
                }
              }}
            >
              Clear
            </Button>
          </Box>
        </Box>
      )}

      {fileProcessed && rcm_data.length > 0 && (
        <Alert 
          severity="success" 
          icon={<CloudDoneIcon />}
          sx={{ 
            mt: 3,
            mb: 2,
            '& .MuiAlert-message': { 
              fontWeight: 400,
              display: 'flex',
              alignItems: 'center'
            } 
          }}
        >
          <Typography variant="body2">
            <strong>{fileName}</strong> with <strong>{rcm_data.length} rows</strong> is ready to upload. Click "Upload RCM" to proceed.
          </Typography>
        </Alert>
      )}
    </Box>
  );
};

export default FileUploadRCM;
